/**
 * 📝 Serviço de respostas de tarefas — workflow_task_responses_v9
 * 
 * Persiste respostas de formulário, checklist, anexos e justificativas
 * de forma normalizada para rastreabilidade total.
 */
import { supabase } from '../../../lib/supabaseClient';

// ============================================================================
// TIPOS
// ============================================================================

export type TaskResponseType = 
  | 'form_field'
  | 'checklist_item'
  | 'attachment'
  | 'justification'
  | 'comment'
  | 'approval_decision';

export interface TaskResponse {
  id: string;
  organizationId: string;
  taskId: string;
  instanceId: string;
  /** Versão do blueprint ativa quando a resposta foi criada */
  blueprintVersionId?: string;
  responseType: TaskResponseType;
  fieldId?: string;
  fieldLabel?: string;
  valueText?: string;
  valueJson?: any;
  valueBoolean?: boolean;
  valueNumber?: number;
  fileName?: string;
  fileUrl?: string;
  fileSizeBytes?: number;
  fileMimeType?: string;
  respondedBy: string;
  respondedByName?: string;
  metadata?: Record<string, any>;
  createdAt: string;
  updatedAt: string;
}

export interface SaveFormResponsesInput {
  organizationId: string;
  taskId: string;
  instanceId: string;
  blueprintVersionId?: string;
  respondedBy: string;
  respondedByName?: string;
  formData: Record<string, unknown>;
  fieldLabels?: Record<string, string>;
}

export interface SaveChecklistResponsesInput {
  organizationId: string;
  taskId: string;
  instanceId: string;
  blueprintVersionId?: string;
  respondedBy: string;
  respondedByName?: string;
  checklistData: Record<string, boolean>;
  itemLabels?: Record<string, string>;
}

export interface SaveAttachmentInput {
  organizationId: string;
  taskId: string;
  instanceId: string;
  blueprintVersionId?: string;
  respondedBy: string;
  respondedByName?: string;
  fileName: string;
  fileUrl: string;
  fileSizeBytes?: number;
  fileMimeType?: string;
}

export interface SaveJustificationInput {
  organizationId: string;
  taskId: string;
  instanceId: string;
  blueprintVersionId?: string;
  respondedBy: string;
  respondedByName?: string;
  justification: string;
  decision?: 'approved' | 'rejected';
}

// ============================================================================
// QUERIES
// ============================================================================

/**
 * Busca todas as respostas de uma task
 */
export async function getTaskResponses(
  taskId: string,
  organizationId: string,
): Promise<TaskResponse[]> {
  const { data, error } = await supabase
    .from('workflow_task_responses_v9')
    .select('*')
    .eq('task_id', taskId)
    .eq('organization_id', organizationId)
    .order('created_at', { ascending: true });

  if (error) {
    console.warn('[taskResponseService] Falha ao buscar respostas:', error.message);
    return [];
  }

  return (data ?? []).map(mapResponseRow);
}

/**
 * Busca respostas de uma instância inteira
 */
export async function getInstanceResponses(
  instanceId: string,
  organizationId: string,
): Promise<TaskResponse[]> {
  const { data, error } = await supabase
    .from('workflow_task_responses_v9')
    .select('*')
    .eq('instance_id', instanceId)
    .eq('organization_id', organizationId)
    .order('created_at', { ascending: true });

  if (error) {
    console.warn('[taskResponseService] Falha ao buscar respostas da instância:', error.message);
    return [];
  }

  return (data ?? []).map(mapResponseRow);
}

// ============================================================================
// MUTATIONS
// ============================================================================

/**
 * Salva respostas de formulário (upsert por field_id)
 */
export async function saveFormResponses(input: SaveFormResponsesInput): Promise<void> {
  const { organizationId, taskId, instanceId, blueprintVersionId, respondedBy, respondedByName, formData, fieldLabels } = input;

  const rows = Object.entries(formData).map(([fieldId, value]) => ({
    organization_id: organizationId,
    task_id: taskId,
    instance_id: instanceId,
    blueprint_version_id: blueprintVersionId ?? null,
    response_type: 'form_field' as const,
    field_id: fieldId,
    field_label: fieldLabels?.[fieldId] ?? fieldId,
    value_text: typeof value === 'string' ? value : null,
    value_number: typeof value === 'number' ? value : null,
    value_boolean: typeof value === 'boolean' ? value : null,
    value_json: typeof value === 'object' && value !== null ? value : null,
    responded_by: respondedBy,
    responded_by_name: respondedByName,
  }));

  if (rows.length === 0) return;

  // Delete existing form responses for this task, then insert new ones
  await supabase
    .from('workflow_task_responses_v9')
    .delete()
    .eq('task_id', taskId)
    .eq('organization_id', organizationId)
    .eq('response_type', 'form_field');

  const { error } = await supabase
    .from('workflow_task_responses_v9')
    .insert(rows);

  if (error) {
    console.warn('[taskResponseService] Falha ao salvar respostas de form:', error.message);
  }
}

/**
 * Salva respostas de checklist (upsert por field_id)
 */
export async function saveChecklistResponses(input: SaveChecklistResponsesInput): Promise<void> {
  const { organizationId, taskId, instanceId, blueprintVersionId, respondedBy, respondedByName, checklistData, itemLabels } = input;

  const rows = Object.entries(checklistData).map(([itemId, checked]) => ({
    organization_id: organizationId,
    task_id: taskId,
    instance_id: instanceId,
    blueprint_version_id: blueprintVersionId ?? null,
    response_type: 'checklist_item' as const,
    field_id: itemId,
    field_label: itemLabels?.[itemId] ?? itemId,
    value_boolean: checked,
    responded_by: respondedBy,
    responded_by_name: respondedByName,
  }));

  if (rows.length === 0) return;

  // Replace existing checklist responses
  await supabase
    .from('workflow_task_responses_v9')
    .delete()
    .eq('task_id', taskId)
    .eq('organization_id', organizationId)
    .eq('response_type', 'checklist_item');

  const { error } = await supabase
    .from('workflow_task_responses_v9')
    .insert(rows);

  if (error) {
    console.warn('[taskResponseService] Falha ao salvar checklist:', error.message);
  }
}

/**
 * Salva um anexo
 */
export async function saveAttachmentResponse(input: SaveAttachmentInput): Promise<void> {
  const { error } = await supabase
    .from('workflow_task_responses_v9')
    .insert({
      organization_id: input.organizationId,
      task_id: input.taskId,
      instance_id: input.instanceId,
      blueprint_version_id: input.blueprintVersionId ?? null,
      response_type: 'attachment',
      file_name: input.fileName,
      file_url: input.fileUrl,
      file_size_bytes: input.fileSizeBytes,
      file_mime_type: input.fileMimeType,
      responded_by: input.respondedBy,
      responded_by_name: input.respondedByName,
    });

  if (error) {
    console.warn('[taskResponseService] Falha ao salvar anexo:', error.message);
  }
}

/**
 * Salva justificativa/decisão de aprovação
 */
export async function saveJustificationResponse(input: SaveJustificationInput): Promise<void> {
  const responseType: TaskResponseType = input.decision ? 'approval_decision' : 'justification';

  const { error } = await supabase
    .from('workflow_task_responses_v9')
    .insert({
      organization_id: input.organizationId,
      task_id: input.taskId,
      instance_id: input.instanceId,
      blueprint_version_id: input.blueprintVersionId ?? null,
      response_type: responseType,
      value_text: input.justification,
      value_json: input.decision ? { decision: input.decision } : null,
      responded_by: input.respondedBy,
      responded_by_name: input.respondedByName,
    });

  if (error) {
    console.warn('[taskResponseService] Falha ao salvar justificativa:', error.message);
  }
}

// ============================================================================
// INTERNAL HELPERS
// ============================================================================

function mapResponseRow(row: any): TaskResponse {
  return {
    id: row.id,
    organizationId: row.organization_id,
    taskId: row.task_id,
    instanceId: row.instance_id,
    blueprintVersionId: row.blueprint_version_id ?? undefined,
    responseType: row.response_type,
    fieldId: row.field_id ?? undefined,
    fieldLabel: row.field_label ?? undefined,
    valueText: row.value_text ?? undefined,
    valueJson: row.value_json ?? undefined,
    valueBoolean: row.value_boolean ?? undefined,
    valueNumber: row.value_number ?? undefined,
    fileName: row.file_name ?? undefined,
    fileUrl: row.file_url ?? undefined,
    fileSizeBytes: row.file_size_bytes ?? undefined,
    fileMimeType: row.file_mime_type ?? undefined,
    respondedBy: row.responded_by,
    respondedByName: row.responded_by_name ?? undefined,
    metadata: row.metadata ?? undefined,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}
